import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useWs } from "@/context/WorkspaceContext";
import { EmptyState, StatusBadge, Avatar, fmtDateTime } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, MessagesSquare, Trash2, Loader2, Mail, Copy, Send, Inbox, Archive, CheckCheck, ArrowLeft, Pencil, Check, X, Users, UserPlus, Ticket, StickyNote, Lock, Link as LinkIcon, Smile, Sparkles, Film, Search, Cookie } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function copyEmail(value) {
  navigator.clipboard?.writeText(value);
  toast.success("Copied to clipboard");
}

const EMOJIS = ["😀","😁","😂","🤣","😊","😍","😎","🤩","🥳","🙌","👏","👍","👎","🙏","💪","🔥","✨","🎉","🚀","💡","✅","❌","⚠️","📈","📉","📊","💰","📅","⏰","📎","✉️","📣","❤️","💜","💚","👀","🤔","😅","😬","🙈","💯","🥇","🎯","🤝","👋","😴","☕"];

function escapeHtml(s) {
  return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderRich(text) {
  let h = escapeHtml(text);
  h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline">$1</a>');
  h = h.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  h = h.replace(/(^|[\s.,!?])_([^_\n]+)_/g, "$1<em>$2</em>");
  h = h.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-black/10 text-[0.85em]">$1</code>');
  h = h.replace(/(^|\s)(@[A-Za-z][\w'-]*)/g, '$1<span class="font-semibold text-[#723FFF]">$2</span>');
  h = h.replace(/\n/g, "<br/>");
  return h;
}

export default function Comms() {
  const { user, canEdit } = useWs();
  const [inboundEmail, setInboundEmail] = useState("");
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [searchParams] = useSearchParams();

  const loadList = async (selectFirst = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/conversations");
      setInboundEmail(data.inbound_email);
      setConvos(data.conversations);
      if (selectFirst && data.conversations.length && !activeId) setActiveId(data.conversations[0].id);
    } finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    setLoadingDetail(true);
    try { const { data } = await api.get(`/conversations/${id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingDetail(false); }
  };

  useEffect(() => { loadList(true); api.get("/workspace/members").then(({ data }) => setMembers(data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const c = searchParams.get("c"); if (c) setActiveId(c); /* eslint-disable-next-line */ }, [searchParams]);
  useEffect(() => { loadDetail(activeId); /* eslint-disable-next-line */ }, [activeId]);
  useEffect(() => {
    if (!activeId) return undefined;
    const t = setInterval(() => {
      api.get(`/conversations/${activeId}/receipt`).then(({ data }) => setDetail((d) => (d && d.id === activeId ? { ...d, read_receipt: data.read_receipt } : d))).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [activeId]);

  const addParticipant = async (userId) => {
    const { data } = await api.post(`/conversations/${activeId}/participants`, { user_id: userId });
    setDetail((d) => d ? { ...d, participants_detail: data.participants_detail } : d);
  };
  const removeParticipant = async (userId) => {
    if (!(await confirmDelete({ message: "Remove this participant from the conversation?", confirmText: "Remove" }))) return;
    try {
      const { data } = await api.delete(`/conversations/${activeId}/participants/${userId}`);
      setDetail((d) => d ? { ...d, participants_detail: data.participants_detail } : d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendMessage = async (body, mentions, gifUrl) => {
    const { data } = await api.post(`/conversations/${activeId}/messages`, { body, mentions, gif_url: gifUrl });
    setDetail((d) => d ? { ...d, messages: [...d.messages, data], read_receipt: { message_id: data.id, seen_by: [] } } : d);
    loadList();
  };

  const createConversation = async (subject, body) => {
    const { data } = await api.post("/conversations", { subject, body });
    setNewOpen(false);
    await loadList();
    setActiveId(data.id);
  };

  const simulate = async () => {
    try {
      await api.post("/conversations/simulate-inbound", null, { params: activeId ? { conversation_id: activeId } : {} });
      toast.success("Inbound email received");
      await loadList();
      if (activeId) loadDetail(activeId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (status) => {
    await api.put(`/conversations/${activeId}/status`, null, { params: { status } });
    setDetail((d) => d ? { ...d, status } : d);
    loadList();
  };

  const remove = async () => {
    if (!(await confirmDelete({ message: "Delete this conversation? This can't be undone." }))) return;
    await api.delete(`/conversations/${activeId}`);
    toast.success("Conversation deleted");
    setActiveId(null); setDetail(null);
    loadList();
  };

  const renameConversation = async (name) => {
    const clean = name.trim();
    if (!clean) return;
    await api.put(`/conversations/${activeId}`, { subject: clean });
    setDetail((d) => d ? { ...d, subject: clean } : d);
    loadList();
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Conversations</h1>
          <p className="mt-2.5 text-sm text-[#9FA1A4] max-w-2xl leading-relaxed">Every thread between your Flight Crew and your team, in one place.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {canEdit && (
            <Button data-testid="simulate-inbound-button" variant="outline" onClick={simulate} className="border-[#EEEFEF] bg-transparent text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C] rounded-full">
              <Inbox className="h-4 w-4 mr-1.5" /> Simulate inbound
            </Button>
          )}
          <NewConversationDialog open={newOpen} setOpen={setNewOpen} onCreate={createConversation} />
        </div>
      </div>

      {user?.account === "agency" && inboundEmail && (
        <button
          data-testid="workspace-inbound-email"
          onClick={() => copyEmail(inboundEmail)}
          title="Agency only. Clients don't see this"
          className="group mb-6 flex items-center gap-3 rounded-2xl border border-[#EEEFEF] bg-[#F6F6FD] px-4 py-3 hover:border-[#C9B8FF] transition-colors w-full sm:w-auto"
        >
          <span className="h-8 w-8 rounded-xl bg-white flex items-center justify-center text-[#723FFF] shrink-0"><Mail className="h-4 w-4" /></span>
          <div className="text-left min-w-0">
            <p className="font-mono-data text-[9px] uppercase tracking-wider text-[#9FA1A4]">CC or forward emails to · agency only</p>
            <p className="font-mono-data text-sm text-[#06060C] truncate">{inboundEmail}</p>
          </div>
          <Copy className="h-3.5 w-3.5 text-[#c4c5cc] group-hover:text-[#723FFF] ml-auto shrink-0" />
        </button>
      )}

      <div className="flex gap-5 h-[calc(100vh-19rem)] min-h-[500px]">
        {/* Left: conversation list */}
        <div className={`w-full md:w-[360px] shrink-0 premium-card p-0 overflow-hidden flex flex-col ${activeId ? "hidden md:flex" : "flex"}`}>
          <div className="px-5 py-4 border-b border-[#f0f0f3] flex items-center justify-between">
            <span className="font-display font-semibold text-[#06060C]">Inbox</span>
            <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{convos.length}</span>
          </div>
          {loading ? (
            <div className="p-5 text-sm text-[#9FA1A4]">Loading…</div>
          ) : convos.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <div className="h-12 w-12 rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mb-3"><MessagesSquare className="h-6 w-6" /></div>
              <p className="text-sm text-[#595D62] font-medium">No conversations yet</p>
              <p className="text-xs text-[#9FA1A4] mt-1">Start one or forward an email to your workspace address.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {convos.map((c) => (
                <button
                  key={c.id}
                  data-testid="conversation-list-item"
                  onClick={() => setActiveId(c.id)}
                  className={`w-full text-left px-5 py-4 border-b border-[#f6f6fa] transition-colors ${activeId === c.id ? "bg-[#F3F0FF]" : "hover:bg-[#F6F6FD]"}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="dot shrink-0" style={{ backgroundColor: c.status === "actioned" ? "#30BD79" : c.status === "archived" ? "#c4c5cc" : "#723FFF" }} />
                    <p className="font-medium text-sm text-[#06060C] truncate flex-1">{c.subject}</p>
                    <span className="font-mono-data text-[10px] text-[#9FA1A4] shrink-0">{timeAgo(c.last_message_at)}</span>
                  </div>
                  <p className="mt-1 text-xs text-[#9FA1A4] truncate pl-4">
                    {c.last_author_name && <span className="text-[#595D62]">{c.last_author_name}: </span>}
                    {c.last_message_preview || "-"}
                  </p>
                  <div className="mt-2 pl-4 flex items-center gap-2">
                    {c.source === "inbound" && <span className="font-mono-data text-[8px] uppercase tracking-wider text-[#723FFF] bg-[#EDE7FF] rounded-full px-1.5 py-0.5">Email</span>}
                    <span className="font-mono-data text-[9px] text-[#c4c5cc]">{c.message_count} msg</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: thread */}
        <div className={`flex-1 premium-card p-0 overflow-hidden flex-col ${activeId ? "flex" : "hidden md:flex"}`}>
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState icon={MessagesSquare} title="Select a conversation" description="Choose a thread on the left to read and reply." testid="comms-empty" />
            </div>
          ) : loadingDetail || !detail ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#9FA1A4]">Loading…</div>
          ) : (
            <>
              {/* thread header */}
              <div className="px-6 py-4 border-b border-[#f0f0f3] flex items-start gap-3">
                <button data-testid="thread-back" onClick={() => setActiveId(null)} className="md:hidden text-[#595D62] mt-1"><ArrowLeft className="h-4 w-4" /></button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <EditableName value={detail.subject} onSave={renameConversation} />
                    <StatusBadge status={detail.status} testid="thread-status" />
                  </div>
                  {detail.is_agency && detail.reply_email && (
                    <button onClick={() => copyEmail(detail.reply_email)} data-testid="thread-reply-email" className="group mt-1 flex items-center gap-1.5 font-mono-data text-[11px] text-[#9FA1A4] hover:text-[#723FFF]">
                      <Mail className="h-3 w-3" /> {detail.reply_email} <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                    </button>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {detail.status !== "actioned" && (
                      <button data-testid="thread-mark-actioned" onClick={() => setStatus("actioned")} title="Mark actioned" className="text-[#9FA1A4] hover:text-[#30BD79] border border-[#EEEFEF] rounded-full p-2"><CheckCheck className="h-4 w-4" /></button>
                    )}
                    <button data-testid="thread-archive" onClick={() => setStatus("archived")} title="Archive" className="text-[#9FA1A4] hover:text-[#06060C] border border-[#EEEFEF] rounded-full p-2"><Archive className="h-4 w-4" /></button>
                    <button data-testid="thread-delete" onClick={remove} title="Delete" className="text-[#c4c5cc] hover:text-rose-600 border border-[#EEEFEF] rounded-full p-2"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>

              {/* messages */}
              <MessageList messages={detail.messages} currentUserId={user.id} receipt={detail.read_receipt} viewerSide={detail.is_agency ? "team" : "client"} />

              {/* composer */}
              <Composer onSend={sendMessage} members={members} />
            </>
          )}
        </div>

        {activeId && detail && (
          <ConversationInfoPanel detail={detail} members={members} onAdd={addParticipant} onRemove={removeParticipant} conversationId={activeId} />
        )}
      </div>
    </div>
  );
}

function ConversationInfoPanel({ detail, members, onAdd, onRemove, conversationId }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [summary, setSummary] = useState(detail.ai_summary || "");
  const [summarizing, setSummarizing] = useState(false);
  const navigate = useNavigate();
  const { slug } = useWs();
  const [tickets, setTickets] = useState(detail.linked_tickets || []);
  const participants = detail.participants_detail || [];
  const participantIds = new Set(participants.map((p) => p.id));
  const addable = members.filter((m) => !participantIds.has(m.id));

  useEffect(() => { setSummary(detail.ai_summary || ""); setTickets(detail.linked_tickets || []); }, [conversationId]);

  const loadNotes = () => { if (detail.is_agency) api.get(`/conversations/${conversationId}/notes`).then(({ data }) => setNotes(data)).catch(() => {}); };
  useEffect(() => { loadNotes(); /* eslint-disable-next-line */ }, [conversationId, detail.is_agency]);

  const genSummary = async () => {
    setSummarizing(true);
    try { const { data } = await api.post(`/conversations/${conversationId}/summary`); setSummary(data.summary); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSummarizing(false); }
  };

  const goCreateTicket = () => {
    navigate(`/${slug}/tickets?new=1&attach=${conversationId}`);
  };

  const addNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try { const { data } = await api.post(`/conversations/${conversationId}/notes`, { body: noteDraft.trim() }); setNotes((n) => [...n, data]); setNoteDraft(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };

  return (
    <div data-testid="conversation-info-panel" className="hidden xl:flex w-80 shrink-0 premium-card p-0 overflow-hidden flex-col">
      <div className="overflow-y-auto">
        {/* AI summary */}
        <div className="p-5 border-b border-[#f0f0f3]">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 font-display font-semibold text-sm text-[#06060C]"><Sparkles className="h-4 w-4 text-[#723FFF]" /> AI summary</span>
            <button data-testid="ai-summary-generate" onClick={genSummary} disabled={summarizing} className="text-xs text-[#723FFF] hover:text-[#5b2fd6] disabled:opacity-50">{summarizing ? "Thinking…" : summary ? "Refresh" : "Generate"}</button>
          </div>
          {summary ? (
            <div data-testid="ai-summary-text" className="text-[13px] text-[#595D62] leading-relaxed whitespace-pre-wrap rounded-xl bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2.5">{summary}</div>
          ) : (
            <p className="text-xs text-[#9FA1A4]">{summarizing ? "Generating a summary…" : "Generate an AI summary of this conversation."}</p>
          )}
        </div>

        {/* Participants */}
        <div className="p-5 border-b border-[#f0f0f3]">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 font-display font-semibold text-sm text-[#06060C]"><Users className="h-4 w-4 text-[#9FA1A4]" /> People</span>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button data-testid="add-participant-button" className="flex items-center gap-1 text-xs text-[#723FFF] hover:text-[#5b2fd6]"><UserPlus className="h-3.5 w-3.5" /> Add</button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 bg-white border-[#EEEFEF] p-1.5 max-h-64 overflow-y-auto">
                {addable.length === 0 ? <p className="text-xs text-[#9FA1A4] p-3 text-center">Everyone's already here.</p> : addable.map((m) => (
                  <button key={m.id} data-testid="participant-option" onClick={() => { onAdd(m.id); setPickerOpen(false); }} className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-[#F6F6FD] text-left">
                    <Avatar name={m.name} size={28} account={m.account} userId={m.id} hasAvatar={m.has_avatar} />
                    <span className="min-w-0"><span className="block text-sm text-[#06060C] truncate">{m.name}</span><span className="block text-[10px] text-[#9FA1A4] truncate">{m.job_title || m.email}</span></span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            {participants.map((p) => (
              <div key={p.id} data-testid="participant-row" className="group relative flex items-center gap-2.5">
                <Avatar name={p.name} size={28} account={p.account} userId={p.id} hasAvatar={p.has_avatar} />
                <div className="min-w-0 flex-1"><p className="text-sm text-[#06060C] truncate leading-tight">{p.name}</p><p className="text-[10px] text-[#9FA1A4] truncate">{p.job_title || (p.is_creator ? "Creator" : (p.account === "agency" ? "Agency" : "Client"))}</p></div>
                {!p.is_creator && <button onClick={() => onRemove(p.id)} className="text-[#c4c5cc] hover:text-rose-600 opacity-0 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>}
                {p.favourite_snack && (
                  <div data-testid="snack-tooltip" className="pointer-events-none absolute left-9 top-full mt-1 z-30 whitespace-nowrap rounded-lg bg-[#06060C] px-2.5 py-1 text-[11px] text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 shadow-lg">
                    <Cookie className="inline h-3 w-3 mr-1 text-[#B2FF59] -mt-0.5" />Can't resist {p.favourite_snack}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Support tickets */}
        <div className="p-5 border-b border-[#f0f0f3]">
          <div className="flex items-center justify-between mb-3">
            <span className="flex items-center gap-2 font-display font-semibold text-sm text-[#06060C]"><Ticket className="h-4 w-4 text-[#9FA1A4]" /> Support tickets</span>
            {detail.is_agency && <button data-testid="create-ticket-button" onClick={goCreateTicket} className="flex items-center gap-1 text-xs font-medium text-[#723FFF] hover:text-[#5b2fd6]"><Plus className="h-3.5 w-3.5" /> Create Support Ticket</button>}
          </div>
          {tickets.length === 0 ? (
            <p className="text-xs text-[#9FA1A4]">No linked tickets yet.</p>
          ) : (
            <div className="space-y-1.5">
              {tickets.map((t) => (
                <div key={t.id} data-testid="linked-ticket" className="rounded-lg border border-[#f0f0f3] bg-[#F6F6FD] px-3 py-2">
                  <p className="text-sm text-[#06060C] truncate mb-1">{t.title}</p>
                  <StatusBadge status={t.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Internal notes (agency only) */}
        {detail.is_agency && (
          <div className="p-5">
            <span className="flex items-center gap-2 font-display font-semibold text-sm text-[#06060C] mb-1"><StickyNote className="h-4 w-4 text-[#9FA1A4]" /> Internal notes</span>
            <p className="flex items-center gap-1 text-[10px] text-[#d19100] mb-3"><Lock className="h-3 w-3" /> Only visible to your agency team</p>
            <div className="space-y-2 mb-3">
              {notes.map((n) => (
                <div key={n.id} data-testid="internal-note" className="rounded-xl bg-[#F9C509]/8 border border-[#F9C509]/25 px-3 py-2">
                  <p className="text-sm text-[#1c1c24] whitespace-pre-wrap break-words">{n.body}</p>
                  <p className="text-[10px] text-[#9FA1A4] mt-1">{n.author_name} · {fmtDateTime(n.created_at)}</p>
                </div>
              ))}
            </div>
            <Textarea data-testid="internal-note-input" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Add a private note…" className="bg-[#F6F6FD] border-[#EEEFEF] min-h-[70px] rounded-xl text-sm resize-none mb-2" />
            <Button data-testid="internal-note-add" onClick={addNote} disabled={savingNote || !noteDraft.trim()} size="sm" className="w-full bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
              {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function EditableName({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = async () => { setEditing(false); if (draft.trim() && draft !== value) { try { await onSave(draft); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } } };
  if (editing) {
    return (
      <span className="flex items-center gap-1">
        <Input data-testid="conversation-name-input" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }} className="h-8 w-56 bg-[#F6F6FD] border-[#EEEFEF] rounded-lg font-display" />
        <button onClick={commit} className="text-[#30BD79] p-1"><Check className="h-4 w-4" /></button>
        <button onClick={() => { setDraft(value); setEditing(false); }} className="text-[#c4c5cc] p-1"><X className="h-4 w-4" /></button>
      </span>
    );
  }
  return (
    <button data-testid="conversation-name" onClick={() => setEditing(true)} className="group flex items-center gap-1.5 min-w-0">
      <h2 className="font-display font-semibold text-[#06060C] truncate">{value}</h2>
      <Pencil className="h-3.5 w-3.5 text-[#c4c5cc] opacity-0 group-hover:opacity-100 shrink-0" />
    </button>
  );
}

function ReadReceipt({ receipt }) {
  const seen = receipt.seen_by || [];
  if (!seen.length) return <span data-testid="read-receipt" className="flex items-center gap-1 font-mono-data text-[10px] text-[#9FA1A4] mt-0.5 px-1"><Check className="h-3 w-3" /> Delivered</span>;
  const latest = seen[seen.length - 1];
  return (
    <span data-testid="read-receipt" className="group/rr relative flex items-center gap-1 font-mono-data text-[10px] text-[#723FFF] mt-0.5 px-1 cursor-default">
      <CheckCheck className="h-3 w-3" /> Seen by {latest.name}{seen.length > 1 ? ` +${seen.length - 1}` : ""} · {fmtDateTime(latest.at)}
      <span data-testid="read-receipt-tooltip" className="pointer-events-none absolute right-0 bottom-full mb-1.5 z-30 min-w-[180px] rounded-xl bg-[#06060C] px-3 py-2 text-[11px] text-white opacity-0 translate-y-1 group-hover/rr:opacity-100 group-hover/rr:translate-y-0 transition-[opacity,transform] duration-200 shadow-lg space-y-1">
        {seen.map((r) => <span key={r.name + r.at} className="flex justify-between gap-4 whitespace-nowrap"><span>{r.name}</span><span className="text-white/60">{fmtDateTime(r.at)}</span></span>)}
      </span>
    </span>
  );
}

function MessageList({ messages, currentUserId, receipt, viewerSide }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  return (
    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5 bg-[#FCFCFE]">
      {messages.map((m) => {
        const own = m.author_id && m.author_id === currentUserId;
        const inbound = m.author_type === "inbound";
        const label = inbound ? "via email" : m.author_type === "team" ? "Team" : "Client";
        return (
          <div key={m.id} data-testid="conversation-message" className={`flex gap-2.5 ${own ? "justify-end" : "justify-start"}`}>
            {!own && <Avatar name={m.author_name} size={30} account={m.author_type === "client" ? "client" : "agency"} userId={m.author_id} hasAvatar={m.author_has_avatar} className="mt-5" />}
            <div className={`max-w-[78%] ${own ? "items-end" : "items-start"} flex flex-col`}>
              <div className="flex items-center gap-2 mb-1 px-1">
                <span className="group/snack relative text-xs font-medium text-[#06060C] cursor-default">{m.author_name}
                  {m.author_snack && (
                    <span className="pointer-events-none absolute left-0 top-full mt-1 z-30 whitespace-nowrap rounded-lg bg-[#06060C] px-2.5 py-1 text-[11px] text-white opacity-0 translate-y-1 group-hover/snack:opacity-100 group-hover/snack:translate-y-0 transition-all duration-200 shadow-lg">
                      <Cookie className="inline h-3 w-3 mr-1 text-[#B2FF59] -mt-0.5" />Can't resist {m.author_snack}
                    </span>
                  )}
                </span>
                {m.author_job_title && <span className="text-[10px] text-[#9FA1A4] hidden sm:inline">· {m.author_job_title}</span>}
                <span className={`font-mono-data text-[8px] uppercase tracking-wider rounded-full px-1.5 py-0.5 ${inbound ? "bg-[#EDE7FF] text-[#723FFF]" : m.author_type === "team" ? "bg-[#06060C] text-white" : "bg-[#F6F6FD] text-[#595D62]"}`}>{label}</span>
              </div>
              <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed break-words ${own ? "bg-[#06060C] text-white rounded-tr-md" : "bg-white border border-[#EEEFEF] text-[#1c1c24] rounded-tl-md"}`}>
                {inbound && m.email_subject && (
                  <div className={`mb-2 pb-2 border-b ${own ? "border-white/15" : "border-[#EEEFEF]"} flex items-center gap-1.5 text-[11px] ${own ? "text-white/70" : "text-[#9FA1A4]"}`}>
                    <Mail className="h-3 w-3 shrink-0" /> <span className="font-medium">Subject:</span> <span className="truncate">{m.email_subject}</span>
                  </div>
                )}
                <span dangerouslySetInnerHTML={{ __html: renderRich(m.body) }} />
                {m.gif_url && <img src={m.gif_url} alt="gif" data-testid="message-gif" className="mt-2 rounded-xl max-w-[240px] w-full" />}
              </div>
              <span className="font-mono-data text-[10px] text-[#c4c5cc] mt-1 px-1">{fmtDateTime(m.created_at)}</span>
              {receipt && receipt.message_id === m.id && (m.author_type === "team" ? "team" : "client") === viewerSide && <ReadReceipt receipt={receipt} />}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

function Composer({ onSend, members = [] }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionIds, setMentionIds] = useState([]);
  const [suggest, setSuggest] = useState(null); // {query, start}
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [gifQuery, setGifQuery] = useState("");
  const [gifs, setGifs] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);
  const ref = useRef(null);

  const searchGifs = async (q) => {
    setGifLoading(true);
    try { const { data } = await api.get("/giphy/search", { params: { q } }); setGifs(data.gifs); }
    catch { toast.error("Couldn't load GIFs"); }
    finally { setGifLoading(false); }
  };
  const sendGif = async (url) => {
    setGifOpen(false);
    try { await onSend("", [], url); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    const active = mentionIds.filter((m) => body.includes(`@${m.token}`)).map((m) => m.id);
    try { await onSend(body.trim(), Array.from(new Set(active))); setBody(""); setMentionIds([]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const onKey = (e) => {
    if (suggest) return;
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const wrap = (before, after = before) => {
    const el = ref.current; if (!el) return;
    const s = el.selectionStart, en = el.selectionEnd;
    const sel = body.slice(s, en) || "text";
    const next = body.slice(0, s) + before + sel + after + body.slice(en);
    setBody(next);
    setTimeout(() => { el.focus(); el.selectionStart = s + before.length; el.selectionEnd = s + before.length + sel.length; }, 0);
  };

  const insertEmoji = (em) => { setBody((b) => b + em); setEmojiOpen(false); setTimeout(() => ref.current?.focus(), 0); };

  const onChange = (e) => {
    const val = e.target.value;
    setBody(val);
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const m = upto.match(/@([A-Za-z][\w'-]*)?$/);
    if (m && members.length) setSuggest({ query: (m[1] || "").toLowerCase(), start: caret - (m[0].length) });
    else setSuggest(null);
  };

  const pickMention = (member) => {
    const token = member.name.split(" ")[0];
    const el = ref.current;
    const caret = el ? el.selectionStart : body.length;
    const next = body.slice(0, suggest.start) + `@${token} ` + body.slice(caret);
    setBody(next);
    setMentionIds((prev) => [...prev, { token, id: member.id }]);
    setSuggest(null);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const filtered = suggest ? members.filter((m) => m.name.toLowerCase().includes(suggest.query)).slice(0, 6) : [];

  return (
    <div className="border-t border-[#f0f0f3] p-4 bg-white relative">
      {suggest && filtered.length > 0 && (
        <div data-testid="mention-dropdown" className="absolute bottom-full left-4 mb-1 w-64 bg-white border border-[#EEEFEF] rounded-xl premium-shadow p-1.5 z-30">
          {filtered.map((m) => (
            <button key={m.id} data-testid="mention-option" onClick={() => pickMention(m)} className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-[#F6F6FD] text-left">
              <Avatar name={m.name} size={24} account={m.account} userId={m.id} hasAvatar={m.has_avatar} />
              <span className="text-sm text-[#06060C] truncate">{m.name}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1 mb-2">
        <button type="button" data-testid="fmt-bold" onClick={() => wrap("**")} className="h-8 w-8 rounded-lg hover:bg-[#F6F6FD] text-[#595D62] font-bold text-sm">B</button>
        <button type="button" data-testid="fmt-italic" onClick={() => wrap("_")} className="h-8 w-8 rounded-lg hover:bg-[#F6F6FD] text-[#595D62] italic text-sm">i</button>
        <button type="button" data-testid="fmt-code" onClick={() => wrap("`")} className="h-8 w-8 rounded-lg hover:bg-[#F6F6FD] text-[#595D62] font-mono text-xs">{"</>"}</button>
        <button type="button" data-testid="fmt-link" onClick={() => wrap("[", "](https://)")} className="h-8 w-8 rounded-lg hover:bg-[#F6F6FD] text-[#595D62] flex items-center justify-center"><LinkIcon className="h-4 w-4" /></button>
        <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
          <PopoverTrigger asChild><button type="button" data-testid="emoji-button" className="h-8 w-8 rounded-lg hover:bg-[#F6F6FD] text-[#595D62] flex items-center justify-center"><Smile className="h-4 w-4" /></button></PopoverTrigger>
          <PopoverContent align="start" className="w-64 bg-white border-[#EEEFEF] p-2">
            <div className="grid grid-cols-8 gap-1">
              {EMOJIS.map((em) => <button key={em} data-testid="emoji-option" onClick={() => insertEmoji(em)} className="h-7 w-7 rounded hover:bg-[#F6F6FD] text-lg leading-none">{em}</button>)}
            </div>
          </PopoverContent>
        </Popover>
        <Popover open={gifOpen} onOpenChange={(o) => { setGifOpen(o); if (o && gifs.length === 0) searchGifs(""); }}>
          <PopoverTrigger asChild><button type="button" data-testid="gif-button" className="h-8 px-2 rounded-lg hover:bg-[#F6F6FD] text-[#595D62] flex items-center justify-center gap-1 text-[11px] font-semibold"><Film className="h-4 w-4" /> GIF</button></PopoverTrigger>
          <PopoverContent align="start" className="w-80 bg-white border-[#EEEFEF] p-2">
            <div className="flex items-center gap-2 rounded-lg border border-[#EEEFEF] bg-[#F6F6FD] px-2.5 mb-2">
              <Search className="h-3.5 w-3.5 text-[#9FA1A4]" />
              <input data-testid="gif-search" value={gifQuery} onChange={(e) => { setGifQuery(e.target.value); searchGifs(e.target.value); }} placeholder="Search Giphy…" className="flex-1 bg-transparent py-2 text-sm outline-none" />
            </div>
            <div className="h-64 overflow-y-auto">
              {gifLoading ? <div className="flex items-center justify-center h-full text-[#9FA1A4]"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
                <div className="grid grid-cols-2 gap-1.5">
                  {gifs.map((g) => <button key={g.id} data-testid="gif-option" onClick={() => sendGif(g.url)} className="rounded-lg overflow-hidden hover:ring-2 hover:ring-[#723FFF]"><img src={g.preview} alt={g.title} className="w-full h-auto block" loading="lazy" /></button>)}
                </div>
              )}
            </div>
            <p className="text-[9px] text-[#c4c5cc] text-center mt-1.5">Powered by GIPHY</p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex items-end gap-2 rounded-2xl border border-[#EEEFEF] bg-[#F6F6FD] p-2 focus-within:border-[#C9B8FF] transition-colors">
        <Textarea
          ref={ref}
          data-testid="composer-input"
          value={body}
          onChange={onChange}
          onKeyDown={onKey}
          placeholder="Write a reply…  Use @ to mention, ** for bold. Enter to send."
          className="flex-1 bg-transparent border-0 focus-visible:ring-0 resize-none min-h-[44px] max-h-40 text-sm shadow-none"
        />
        <Button data-testid="composer-send" onClick={submit} disabled={!body.trim() || sending} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full h-10 w-10 p-0 shrink-0">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function NewConversationDialog({ open, setOpen, onCreate }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSaving(true);
    try { await onCreate(subject.trim(), body.trim()); setSubject(""); setBody(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-conversation-button" className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
          <Plus className="h-4 w-4 mr-1.5" /> New conversation
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C]">
        <DialogHeader><DialogTitle className="font-display">Start a conversation</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Conversation name</label>
            <Input data-testid="new-conversation-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Spring campaign planning" className="bg-[#F6F6FD] border-[#EEEFEF] rounded-xl" />
          </div>
          <div>
            <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Message</label>
            <Textarea data-testid="new-conversation-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your first message…" className="bg-[#F6F6FD] border-[#EEEFEF] min-h-[120px] rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="new-conversation-submit" onClick={submit} disabled={saving || !subject.trim() || !body.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start conversation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
